import useGlobalVolume from "app/core/hooks/useGlobalVolume"
import useFilePathCallback from "app/files/hooks/useFilePathCallback"
import { applyMediaVolume } from "app/player/utils/applyMediaVolume"
import { useAtom, useAtomValue, useSetAtom } from "jotai"
import { useAtomCallback } from "jotai/utils"
import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
} from "react"
import shuffleArray from "utils/global/shuffleArray"
import {
  LessonWithCard,
  PlayingState,
  PlaylistInfoState,
} from "../../playlists/types"
import {
  audioCurrentTimeAtom,
  audioDurationAtom,
  audioElementRefAtom,
  audioLoopAtom,
  audioShuffleAtom,
  currentPlayingItemAtom,
  indexAtom,
  isLoadingAtom,
  isMutedAtom,
  isPlayingAtom,
  nextTrackAtom,
  playingStateAtom,
  playlistAtom,
} from "../state"
import Audio from "./Audio"

export function useWindowEvent<K extends keyof WindowEventMap>(
  type: K,
  listener: (this: Window, ev: WindowEventMap[K]) => any,
  options?: boolean | AddEventListenerOptions
) {
  useEffect(() => {
    window.addEventListener(type, listener, options)
    return () => window.removeEventListener(type, listener, options)
  }, [listener, options, type])
}

const PlayerContext = createContext<HTMLAudioElement | null>(null)

export const useTogglePlaySpace = () => {
  const { togglePlay } = usePlay()
  const player = useContext(PlayerContext)
  const playingState = useAtomValue(playingStateAtom)

  useEffect(() => {
    if (!player || !playingState) return
    const onKeyPress = (e: KeyboardEvent) => {
      if (
        e.key === " " &&
        (!(e.target instanceof HTMLElement) ||
          (e.target?.tagName !== "INPUT" && e.target?.tagName !== "TEXTAREA"))
      ) {
        e.preventDefault()
        togglePlay()
      }
    }

    window.addEventListener("keypress", onKeyPress)

    return () => {
      window.removeEventListener("keypress", onKeyPress)
    }
  }, [player, playingState, togglePlay])

  return null
}

type Props = PropsWithChildren<{}>

export const PlayerProvider = ({ ...props }: Props) => {
  const [isPlaying, setIsPlaying] = useAtom(isPlayingAtom)
  const setIsLoading = useSetAtom(isLoadingAtom)
  const playingState = useAtomValue(playingStateAtom)
  const currentPlayingItem = useAtomValue(currentPlayingItemAtom)
  const getFilePath = useFilePathCallback()
  const ref = useAtomValue(audioElementRefAtom)
  const [isMuted, setMuted] = useAtom(isMutedAtom)
  const setCurrentTime = useSetAtom(audioCurrentTimeAtom)
  const setDuration = useSetAtom(audioDurationAtom)
  const [loop, setLoop] = useAtom(audioLoopAtom)

  useEffect(() => {
    if (!ref) return
    if (!currentPlayingItem) return
    if (ref.paused) return
    const url = currentPlayingItem?.lesson?.radioSong
      ? getFilePath(currentPlayingItem.lesson?.radioSong)
      : null
    if (url && ref.src !== url) {
      ref.src = url
      ref.play()
    }
  }, [ref, playingState, getFilePath, currentPlayingItem])

  return (
    <PlayerContext.Provider {...props} value={ref}>
      <Audio
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onLoadStart={() => setIsLoading(true)}
        onCanPlay={() => setIsLoading(false)}
        muted={isMuted}
        onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
        onDurationChange={(e) => setDuration(e.currentTarget.duration)}
        onLoadedMetadata={(e) => {
          setDuration(e.currentTarget.duration)
        }}
        loop={loop}
      />
      {props.children}
    </PlayerContext.Provider>
  )
}

export const usePlay = () => {
  const ref = useContext(PlayerContext)
  const setIsPlaying = useSetAtom(isPlayingAtom)
  const [volume] = useGlobalVolume()
  const getFilePath = useFilePathCallback()
  const playingState = useAtomValue(playingStateAtom)

  // Update volume when atom updates
  useEffect(() => {
    if (!ref) return
    applyMediaVolume(ref, volume)
  }, [ref, volume])

  const getUrl = useCallback(
    (lesson: LessonWithCard) => {
      if (!lesson) return null
      if (lesson?.radioSong) {
        return getFilePath(lesson?.radioSong)
      }
      if (lesson?.rehearse?.video) {
        return getFilePath(lesson.rehearse?.video)
      }
      if (lesson?.rehearse?.audio) {
        return getFilePath(lesson.rehearse?.audio)
      }
      return null
    },
    [getFilePath]
  )

  // Set url
  useEffect(() => {
    if (!playingState || !ref || playingState.type !== "VISIBLE") return
    const url = getUrl(playingState.list[playingState.index].lesson)
    if (!url || ref.src === url) return
    ref.src = url
  }, [getUrl, playingState, ref])

  const play = useCallback(() => {
    if (!ref) return
    // ref.src = ref.src
    ref
      .play()
      .catch((e) => {
        console.log("e", e)
        setIsPlaying(false)
      })
      .then(() => {})
  }, [ref, setIsPlaying])

  const pause = useCallback(() => {
    if (!ref) return
    ref.pause()
  }, [ref])

  return {
    play,
    pause,
    getUrl,
    ref,
    togglePlay: () => {
      if (!ref) return
      if (ref.paused) {
        play()
      } else {
        pause()
      }
    },
  }
}
